import { Component, DestroyRef, effect, inject, NO_ERRORS_SCHEMA, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	Icons,
	ITableConfig,
	ITableHeader,
	ITablePagination,
	ITableRow,
	ITableSortEvent,
	LinkComponent,
	ModalComponent,
	TableComponent,
} from '@tecalisdev/infinity';
import { Subscription } from 'rxjs/internal/Subscription';
import { finalize } from 'rxjs';
import { formatFilterTable } from '../../../core/utils/utils';
import { OPERATION_TYPE } from '../../../operations/models/operations.models';
import { PROCESS_STATUS } from '../../../processes/models/processes.models';
import { IMetaResponse } from '../../../shared/models/master.models';
import { LoadingService } from '../../../shared/services/loading.service';
import { MasterService } from '../../../shared/services/master.service';
import { NavigationService } from '../../../shared/services/navigation.service';
import {
	IRejectedByErrorCodeBody,
	IRejectedByErrorCodePayload,
	RejectedByErrorCodeSortBy,
} from '../../models/panel.models';
import { PanelHelperService } from '../../services/panel-helper.service';
import { PanelService } from '../../services/panel.service';

const TOP_REJECTION_REASONS_LIMIT = 5;
const MODAL_PAGE_SIZE = 10;
const PREVIEW_COLUMNS = 2;
const MODAL_COLUMNS = 3;

/** Claves UI de cabecera → campos de ordenación del API. */
const UI_SORT_TO_API: Record<string, RejectedByErrorCodeSortBy> = {
	reason: 'error_code',
	operation: 'operation_type_uuid',
	count: 'processes',
};

interface IRejectionReasonRow {
	error_code: number;
	operation_type_uuid: string;
	reasonLabel: string;
	operationLabel: string;
	operationIcon: Icons;
	count: number;
}

/** Payload de fila para deep-link a listado Procesos (NIF-1005). */
interface IRejectionReasonClickValue {
	error_code: number;
	operation_type_uuid: string;
}

@Component({
	selector: 'app-rejection-reasons',
	imports: [TranslatePipe, TableComponent, LinkComponent, ModalComponent],
	templateUrl: './rejection-reasons.component.html',
	styleUrl: './rejection-reasons.component.scss',
	schemas: [NO_ERRORS_SCHEMA],
})
export class RejectionReasonsComponent {
	private panelService = inject(PanelService);
	private panelHelperService = inject(PanelHelperService);
	private masterService = inject(MasterService);
	private loading = inject(LoadingService);
	private trans = inject(TranslateService);
	private destroyRef = inject(DestroyRef);
	private navigationService = inject(NavigationService);

	public readonly previewRows = signal<ITableRow[]>([]);
	public readonly modalRows = signal<ITableRow[]>([]);
	public readonly viewAllOpen = signal(false);
	public readonly hasMore = signal(false);

	private dashboardFilters: Record<string, unknown> = {};
	private sort: ITableSortEvent = {
		sortKey: 'count',
		sortDirection: 'desc',
		headerIndex: 2,
	};

	public previewConfig: ITableConfig = { clickeable: true, checkbox: false, borderless: true };
	public modalConfig: ITableConfig = this.buildModalConfig(0);
	public modalHeader: ITableHeader[] = this.buildModalHeader();

	private modalRequest: Subscription | null = null;
	/** Última página pedida al API. No usar `config.pagination.page`: tec-table muta ese objeto antes del emit. */
	private lastModalPage = 1;
	private lastModalLimit = MODAL_PAGE_SIZE;

	private readonly operationTypeIcons: Record<string, Icons> = {
		[OPERATION_TYPE.OCR]: 'icon-card',
		[OPERATION_TYPE.POL]: 'icon-user',
		[OPERATION_TYPE.DOCUMENTS]: 'icon-file',
		[OPERATION_TYPE.CONNECTIONS]: 'icon-connection',
		[OPERATION_TYPE.FINANCIAL_EVALUATION]: 'icon-coin',
		[OPERATION_TYPE.EVALUATION]: 'icon-connection',
		[OPERATION_TYPE.BORME]: 'icon-file',
		[OPERATION_TYPE.FORM]: 'icon-fill-form',
		[OPERATION_TYPE.TFA]: 'icon-message-circle',
		'2FA': 'icon-message-circle',
		[OPERATION_TYPE.SMS]: 'icon-message-circle',
		[OPERATION_TYPE.WHATSAPP]: 'icon-message-circle',
	};

	constructor() {
		// NIF-975: motivos KO para cualquier rol con acceso al panel (no solo superadmin).
		// Solo filtros del panel: no depender de viewAllOpen (evita N peticiones al abrir la modal).
		effect(() => {
			const rawFilters = this.panelHelperService.currentFilters();
			if (!rawFilters) {
				return;
			}
			this.dashboardFilters = { ...formatFilterTable(rawFilters) };
			this.loadPreview();

			const modalOpen = untracked(() => this.viewAllOpen());
			if (modalOpen) {
				this.modalConfig.pagination = {
					...this.modalConfig.pagination,
					page: 1,
				};
				this.loadModalPage();
			} else {
				this.resetModalState(0);
			}
		});

		// NIF-748: title/message vienen del API (Accept-Language); hay que recargar al cambiar idioma.
		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.modalHeader = this.buildModalHeader();
			if (!Object.keys(this.dashboardFilters).length) {
				return;
			}
			this.loadPreview();
			if (this.viewAllOpen()) {
				this.loadModalPage();
			}
		});
	}

	public openViewAll(): void {
		this.modalConfig.pagination = {
			...this.modalConfig.pagination,
			page: 1,
		};
		this.sort = { sortKey: 'count', sortDirection: 'desc', headerIndex: 2 };
		this.modalHeader = this.buildModalHeader();
		this.viewAllOpen.set(true);
		this.loadModalPage();
	}

	public closeViewAll(): void {
		this.viewAllOpen.set(false);
		this.cancelModalRequest();
		this.loading.hideTable(this.modalConfig);
	}

	/** NIF-1005: click en motivo → Procesos con KO + motivo + operación + filtros del panel. */
	public onRowClick(row: ITableRow & { event?: MouseEvent }): void {
		const click = row?.clickValue as IRejectionReasonClickValue | null | undefined;
		if (!click?.error_code || !click.operation_type_uuid) {
			return;
		}
		const queryParams: Record<string, string> = {
			status: PROCESS_STATUS.KO,
			error_code: String(click.error_code),
			operation_type_uuids: click.operation_type_uuid,
		};
		const startAt = this.dashboardFilters['start_at'];
		const endAt = this.dashboardFilters['end_at'];
		if (typeof startAt === 'string' && startAt) {
			queryParams['start_at'] = startAt;
		}
		if (typeof endAt === 'string' && endAt) {
			queryParams['end_at'] = endAt;
		}
		const companyRaw = this.dashboardFilters['company_uuid'];
		const companyUuid =
			companyRaw != null && typeof companyRaw === 'object' && 'id' in (companyRaw as object)
				? String((companyRaw as { id: string | number }).id)
				: companyRaw != null && companyRaw !== ''
					? String(companyRaw)
					: '';
		if (companyUuid) {
			queryParams['company_uuid'] = companyUuid;
		}
		this.navigationService.navigateTo(
			{ routeSegments: ['processes'], extras: { queryParams } },
			row.event,
		);
	}

	public paginationChange(pagination: ITablePagination): void {
		const meta = pagination as ITablePagination & Partial<IMetaResponse>;
		const page = Number(meta.current_page ?? meta.page) || 1;
		const limit =
			Number(meta.per_page ?? meta.limit) || this.lastModalLimit || MODAL_PAGE_SIZE;
		// Comparar con last* no con config.pagination: Infinity muta page/limit in-place y luego emite.
		if (page === this.lastModalPage && limit === this.lastModalLimit) {
			return;
		}
		this.modalConfig.pagination = {
			...this.modalConfig.pagination,
			page,
			limit,
		};
		this.loadModalPage();
	}

	public sortChange(event: ITableSortEvent): void {
		const next: ITableSortEvent = !event.sortDirection
			? { sortKey: 'count', sortDirection: 'desc', headerIndex: 2 }
			: event;
		if (next.sortKey === this.sort.sortKey && next.sortDirection === this.sort.sortDirection) {
			return;
		}
		this.sort = next;
		this.modalConfig.pagination = {
			...this.modalConfig.pagination,
			page: 1,
		};
		this.loadModalPage();
	}

	private loadPreview(): void {
		this.loading.showTable(this.previewConfig, PREVIEW_COLUMNS);
		this.previewConfig.loadingRows = TOP_REJECTION_REASONS_LIMIT;

		this.panelService
			.getRejectedByErrorCode(this.buildPayload(1, TOP_REJECTION_REASONS_LIMIT))
			.pipe(finalize(() => this.loading.hideTable(this.previewConfig)))
			.subscribe({
				next: (response) => {
					const data = Array.isArray(response?.data) ? response.data : [];
					const total = Number(response?.meta?.total) || data.length;
					this.previewRows.set(this.toPreviewTableRows(this.toLabeledItems(data)));
					this.hasMore.set(total > TOP_REJECTION_REASONS_LIMIT);
				},
				error: () => {
					this.previewRows.set([]);
					this.hasMore.set(false);
				},
			});
	}

	private loadModalPage(): void {
		this.cancelModalRequest();
		const page = this.modalConfig.pagination?.page || 1;
		const limit = this.modalConfig.pagination?.limit || MODAL_PAGE_SIZE;
		this.lastModalPage = page;
		this.lastModalLimit = limit;
		this.loading.showTable(this.modalConfig, MODAL_COLUMNS);
		this.modalConfig.loadingRows = limit;

		this.modalRequest = this.panelService
			.getRejectedByErrorCode(this.buildPayload(page, limit))
			.pipe(
				finalize(() => {
					this.loading.hideTable(this.modalConfig);
					this.modalRequest = null;
				}),
			)
			.subscribe({
				next: (response) => {
					const data = Array.isArray(response?.data) ? response.data : [];
					const total = Number(response?.meta?.total) || 0;
					const startIndex = this.rankingStart(response?.meta, page, limit);
					this.modalHeader = this.buildModalHeader();
					this.modalRows.set(this.toModalTableRows(this.toLabeledItems(data), startIndex));
					const nextPage = Number(response?.meta?.current_page) || page;
					const nextLimit = Number(response?.meta?.per_page) || limit;
					this.modalConfig.pagination = {
						...this.modalConfig.pagination,
						page: nextPage,
						limit: nextLimit,
						total,
					};
				},
				error: () => {
					this.modalRows.set([]);
					this.modalConfig.pagination = {
						...this.modalConfig.pagination,
						total: 0,
					};
				},
			});
	}

	private cancelModalRequest(): void {
		if (this.modalRequest) {
			this.modalRequest.unsubscribe();
			this.modalRequest = null;
		}
	}

	private buildPayload(page: number, perPage: number): IRejectedByErrorCodePayload {
		return {
			...(this.dashboardFilters as IRejectedByErrorCodePayload),
			page,
			per_page: perPage,
			sort_by: UI_SORT_TO_API[this.sort.sortKey] || 'processes',
			sort_order: this.sort.sortDirection === 'asc' ? 'asc' : 'desc',
		};
	}

	private rankingStart(
		meta: { from?: number; current_page?: number; per_page?: number } | undefined,
		page: number,
		limit: number,
	): number {
		if (typeof meta?.from === 'number' && meta.from > 0) {
			return meta.from - 1;
		}
		const currentPage = Number(meta?.current_page) || page;
		const perPage = Number(meta?.per_page) || limit;
		return (currentPage - 1) * perPage;
	}

	private toLabeledItems(data: IRejectedByErrorCodeBody[]): IRejectionReasonRow[] {
		const items: IRejectionReasonRow[] = [];
		for (const item of data) {
			const code = Number(item.error_code);
			if (!Number.isFinite(code) || code <= 0) {
				continue;
			}
			const i18nKey = `ERRORS.PROCESS.TITLE_${code}`;
			const payloadTitle = item.title?.trim() || '';
			const translated = this.trans.instant(i18nKey);
			const title = payloadTitle || (translated !== i18nKey ? translated : '');
			const reasonLabel =
				title || `${this.trans.instant('ERRORS.PROCESS.TITLE_GENERIC')} (${code})`;
			const operationType = this.resolveOperationType(item.operation_type_uuid);
			items.push({
				error_code: code,
				operation_type_uuid: (item.operation_type_uuid || '').trim(),
				reasonLabel,
				operationLabel: operationType.label,
				operationIcon: operationType.icon,
				count: Number(item.processes) || 0,
			});
		}
		return items;
	}

	private resolveOperationType(operationTypeUuid: string | undefined): { label: string; icon: Icons } {
		const uuid = (operationTypeUuid || '').trim();
		if (!uuid) {
			return { label: '-', icon: 'icon-file' };
		}
		const found = this.masterService.operationsTypes?.find((type) => String(type.id) === uuid);
		const typeName = typeof found?.value === 'string' ? found.value : '';
		return {
			label: found?.label || '-',
			icon: this.operationTypeIcons[typeName] ?? 'icon-file',
		};
	}

	/** Preview panel (Figma 11737:7683): 2 cols — motivo + subtítulo operación | cantidad. */
	private toPreviewTableRows(items: IRejectionReasonRow[]): ITableRow[] {
		return items.map((item, index) => ({
			clickValue: this.toClickValue(item),
			cells: [
				{
					label: item.reasonLabel,
					secondaryLabel: item.operationLabel !== '-' ? item.operationLabel : undefined,
					badge: { label: (index + 1).toString(), status: 'on', type: 'default' },
				},
				this.toCountCell(item.count),
			],
		}));
	}

	/** Modal (Figma 11605:53178): Motivo | Operación | Nº oper. + ranking según meta.from. */
	private toModalTableRows(items: IRejectionReasonRow[], startIndex: number): ITableRow[] {
		return items.map((item, index) => ({
			clickValue: this.toClickValue(item),
			cells: [
				{
					label: item.reasonLabel,
					badge: { label: (startIndex + index + 1).toString(), status: 'on', type: 'default' },
				},
				{
					label: item.operationLabel,
					tagImage: { icon: item.operationIcon, status: 'simple' },
				},
				this.toCountCell(item.count),
			],
		}));
	}

	private toClickValue(item: IRejectionReasonRow): IRejectionReasonClickValue | null {
		if (!item.error_code || !item.operation_type_uuid) {
			return null;
		}
		return {
			error_code: item.error_code,
			operation_type_uuid: item.operation_type_uuid,
		};
	}

	private toCountCell(count: number) {
		return {
			label: '',
			badge: { label: count.toString(), type: 'square' as const },
			align: 'right' as const,
			classes: ['rejection-reasons__count-cell'],
		};
	}

	private buildModalHeader(): ITableHeader[] {
		const activeKey = this.sort.sortKey;
		const direction = this.sort.sortDirection;
		return [
			{
				label: this.trans.instant('PANEL.REJECTION_REASONS.COLUMN_REASON'),
				sortable: true,
				sortKey: 'reason',
				sortDirection: activeKey === 'reason' ? direction : null,
			},
			{
				label: this.trans.instant('PANEL.REJECTION_REASONS.COLUMN_OPERATION'),
				sortable: true,
				sortKey: 'operation',
				sortDirection: activeKey === 'operation' ? direction : null,
			},
			{
				label: this.trans.instant('PANEL.REJECTION_REASONS.COLUMN_COUNT'),
				sortable: true,
				sortKey: 'count',
				sortDirection: activeKey === 'count' ? direction : null,
				align: 'right',
			},
		];
	}

	private buildModalConfig(total: number): ITableConfig {
		return {
			clickeable: true,
			checkbox: false,
			pagination: {
				page: 1,
				limit: MODAL_PAGE_SIZE,
				total,
				label: this.trans.instant('TABLE.ELEMENTS_BY_PAGE'),
				elementsByPage: [
					{ id: '10', label: '10' },
					{ id: '25', label: '25' },
					{ id: '50', label: '50' },
				],
			},
		};
	}

	private resetModalState(total: number): void {
		this.modalConfig = this.buildModalConfig(total);
		this.lastModalPage = 1;
		this.lastModalLimit = MODAL_PAGE_SIZE;
		this.sort = { sortKey: 'count', sortDirection: 'desc', headerIndex: 2 };
		this.modalHeader = this.buildModalHeader();
		this.modalRows.set([]);
	}
}
